// src/components/TeamMatchesModal.js
import React, { useEffect, useState } from 'react';
import teamService from '../services/teamService';
import matchService from '../services/matchService';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../utils/analytics';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Box,
	TablePagination,
	Tooltip as MuiTooltip,
	// Přidané importy pro Select
	FormControl,
	InputLabel,
	Select,
	MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import TeamBadge from './TeamBadge';

// Pomocná funkce pro převod názvu ligy na klíč pro i18n
const slugifyLeague = (name) => {
	if (!name) return '';
	return name.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, '_');
};

// Z týmových statistik (API-FOOTBALL) vytáhneme počet červených karet – stejně
// jako na MatchCard. Statistiky se dotahují zvlášť přes /matches/:id/statistics.
const getRedCardCount = (team) => {
	const found = team?.statistics?.find((s) => s.type === 'Red Cards');
	const n = found ? parseInt(found.value, 10) : 0;
	return Number.isNaN(n) ? 0 : n;
};

const extractRedCards = (stats, match) => {
	const teams = stats?.teams || [];
	const norm = (s) => (s || '').trim().toLowerCase();
	const home = teams.find((tm) => norm(tm.teamName) === norm(match.homeTeam.name)) || teams[0];
	const away = teams.find((tm) => norm(tm.teamName) === norm(match.awayTeam.name)) || teams[1];
	return { home: getRedCardCount(home), away: getRedCardCount(away) };
};

const TeamMatchesModal = ({ teamId, teamName, onClose, open }) => {
	const [matches, setMatches] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedLeague, setSelectedLeague] = useState('all'); // Nový stav pro filtr
	// Mapa matchId -> { home, away } s počty červených karet. Plní se líně jen pro
	// právě zobrazenou stránku tabulky.
	const [redCardsByMatch, setRedCardsByMatch] = useState({});
	const { t } = useTranslation();

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const [animatedGoalsScored, setAnimatedGoalsScored] = useState(0);
	const [animatedGoalsReceived, setAnimatedGoalsReceived] = useState(0);

	const isMobile = useMediaQuery('(max-width:768px)');

	useEffect(() => {
		if (isMobile && rowsPerPage !== 5) {
			setRowsPerPage(5);
			setPage(0);
		}
	}, [isMobile]);

	useEffect(() => {
		const loadMatches = async () => {
			setIsLoading(true);
			try {
				const data = await teamService.getLastMatchesByTeam(teamId);
				setMatches(data);
				setRedCardsByMatch({});
				setPage(0);
			} catch (err) {
				console.error(err);
			} finally {
				setIsLoading(false);
			}
		};

		if (teamId) {
			loadMatches();
		}
	}, [teamId]);

	useEffect(() => {
		if (teamId && open) {
			trackEvent('Team History Viewed', {
				team_id: teamId,
				sport: 'unknown',
				source: 'match_card'
			});
		}
	}, [teamId, open]);

	const handleClose = () => {
		onClose();
	};

	// Handler pro změnu ligy
	const handleLeagueChange = (event) => {
		setSelectedLeague(event.target.value);
		setPage(0); // Reset stránkování při změně filtru
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// --- FILTROVÁNÍ DAT ---
	// Získání unikátních lig ze stažených zápasů
	const uniqueLeagues = [...new Set(matches.map(m => m.leagueName).filter(Boolean))].sort();

	// Aplikace filtru na zápasy
	const filteredMatches = selectedLeague === 'all'
		? matches
		: matches.filter(m => m.leagueName === selectedLeague);

	// Pagination nyní používá filteredMatches místo matches
	const paginatedMatches = filteredMatches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	// Statistiky se počítají z posledních 5 zápasů (respektují filtr)
	const lastFiveMatches = filteredMatches.slice(0, 5);

	// Líně dotáhneme červené karty pro zápasy na aktuální stránce tabulky. Stejně
	// jako MatchCard je tahá zvlášť přes /matches/:id/statistics. Odložené (PST/POST)
	// zápasy přeskakujeme – nemají statistiky.
	useEffect(() => {
		if (isLoading || matches.length === 0) return;
		let cancelled = false;
		const toFetch = paginatedMatches.filter((m) =>
			redCardsByMatch[m.id] === undefined &&
			m.matchStatus !== 'PST' && m.matchStatus !== 'POST'
		);
		if (toFetch.length === 0) return;

		Promise.all(
			toFetch.map(async (m) => {
				const stats = await matchService.getMatchStatistics(m.id);
				const rc = stats && stats.teams ? extractRedCards(stats, m) : { home: 0, away: 0 };
				return { id: m.id, rc };
			})
		).then((results) => {
			if (cancelled) return;
			setRedCardsByMatch((prev) => {
				const next = { ...prev };
				results.forEach((r) => { next[r.id] = r.rc; });
				return next;
			});
		});

		return () => { cancelled = true; };
		// paginatedMatches je odvozené z těchto závislostí; po naplnění mapy se efekt
		// znovu spustí, ale toFetch už bude prázdné, takže se nic nedotahuje.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, matches, selectedLeague, page, rowsPerPage, redCardsByMatch]);

	const stats = lastFiveMatches.reduce(
		(acc, m) => {
			const isHome = m.homeTeam.id === teamId;
			const goalsFor = isHome ? m.scoreHome : m.scoreAway;
			const goalsAgainst = isHome ? m.scoreAway : m.scoreHome;

			if (m.matchStatus !== 'PST' || m.matchStatus !== 'POST') {
				if (goalsFor > goalsAgainst) acc.wins++;
				else if (goalsFor < goalsAgainst) acc.losses++;
				else acc.draws++;

				acc.totalGoalsFor += goalsFor;
				acc.totalGoalsAgainst += goalsAgainst;
				acc.playedMatches++;
			}
			return acc;
		},
		{ wins: 0, losses: 0, draws: 0, totalGoalsFor: 0, totalGoalsAgainst: 0, playedMatches: 0 }
	);

	const avgGoalsScored = stats.playedMatches > 0 ? (stats.totalGoalsFor / stats.playedMatches) : 0;
	const avgGoalsReceived = stats.playedMatches > 0 ? (stats.totalGoalsAgainst / stats.playedMatches) : 0;

	useEffect(() => {
		if (!isLoading && open && (avgGoalsScored > 0 || avgGoalsReceived > 0)) {
			const duration = 400;
			const steps = 30;
			const stepDuration = duration / steps;

			let currentStep = 0;

			const animate = () => {
				currentStep++;
				const progress = currentStep / steps;
				const easeOut = 1 - Math.pow(1 - progress, 3);

				setAnimatedGoalsScored(avgGoalsScored * easeOut);
				setAnimatedGoalsReceived(avgGoalsReceived * easeOut);

				if (currentStep < steps) {
					setTimeout(animate, stepDuration);
				} else {
					setAnimatedGoalsScored(avgGoalsScored);
					setAnimatedGoalsReceived(avgGoalsReceived);
				}
			};

			setAnimatedGoalsScored(0);
			setAnimatedGoalsReceived(0);
			setTimeout(animate, 900);
		}
	}, [isLoading, open, avgGoalsScored, avgGoalsReceived]);

	const pieData = [
		{ name: t('winLong'), value: stats.wins, color: '#4CAF50' },
		{ name: t('lostLong'), value: stats.losses, color: '#d9534f' },
		{ name: t('drawLong'), value: stats.draws, color: '#888' },
	].filter(item => item.value > 0);

	const effectivePaddingAngle = pieData.length > 1 ? 6 : 0;

	// Vrátí goalsFor/goalsAgainst tak, aby pro fotbalová utkání rozhodnutá penaltami
	// (PEN, kde základní hrací doba skončila remízou) vycházel vítěz z penaltového skóre.
	const getEffectiveGoals = (match) => {
		const isHome = match.homeTeam.id === teamId;
		const goalsFor = isHome ? match.scoreHome : match.scoreAway;
		const goalsAgainst = isHome ? match.scoreAway : match.scoreHome;

		if (match.matchStatus === 'PEN' && goalsFor === goalsAgainst &&
			match.penaltyScoreHome != null && match.penaltyScoreAway != null) {
			return {
				goalsFor: isHome ? match.penaltyScoreHome : match.penaltyScoreAway,
				goalsAgainst: isHome ? match.penaltyScoreAway : match.penaltyScoreHome,
			};
		}
		return { goalsFor, goalsAgainst };
	};

	const getResultStyle = (match) => {
		if (match.matchStatus === 'PST' || match.matchStatus === 'POST') return '#FF9800';

		const { goalsFor, goalsAgainst } = getEffectiveGoals(match);

		const isOtOrPenalties = match.matchStatus === 'AOT' || match.matchStatus === 'AP' || match.matchStatus === 'AET' || match.matchStatus === 'PEN';

		if (goalsFor > goalsAgainst) {
			if (isOtOrPenalties) {
				return 'linear-gradient(135deg, #4CAF50 50%, #888 50%)';
			}
			return '#4CAF50';
		}

		if (goalsFor < goalsAgainst) {
			if (isOtOrPenalties) {
				return 'linear-gradient(135deg, #d9534f 50%, #888 50%)';
			}
			return '#d9534f';
		}
		return '#888';
	};

	const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
		const RADIAN = Math.PI / 180;
		const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);

		return (
			<text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={isMobile ? 12 : 14}>
				{value}
			</text>
		);
	};

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear().toString().slice(-2);
		return `${day}.${month}.${year}`;
	};

	// Malá červená karta vedle názvu týmu, který v daném zápase dostal červenou.
	// Vzhled odpovídá kartě na MatchCard (.red-card-badge).
	const renderRedCard = (count) => {
		if (!count || count < 1) return null;
		return (
			<MuiTooltip title={t('matchCard.redCard', { defaultValue: 'Červená karta' })} arrow>
				<Box
					component="span"
					sx={{
						flexShrink: 0,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 11,
						height: 15,
						background: '#dc2626',
						borderRadius: '2px',
						boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
						color: '#fff',
						fontSize: 9,
						fontWeight: 700,
						lineHeight: 1,
					}}
				>
					{count > 1 && <Box component="span" sx={{ transform: 'translateY(0.5px)' }}>{count}</Box>}
				</Box>
			</MuiTooltip>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ m: 0, p: isMobile ? 1.5 : 2, position: 'relative' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', pr: 5 }}>
					<Box sx={{ mr: 2 }}>
						<TeamBadge teamId={teamId} teamName={teamName} simple />
					</Box>
					<Typography
						variant="h6"
						sx={{
							fontSize: isMobile ? '16px' : '20px',
							fontWeight: 700,
						}}
					>
						{t('lastMatchesOfTeam')} {t(`teams.${teamName}`, { defaultValue: teamName })}
					</Typography>
				</Box>
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{isLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : matches.length === 0 ? (
					<Typography sx={{ textAlign: 'center', p: 2 }}>{t('noMatchesOfTeam')}</Typography>
				) : (
					<>
						<Box sx={{ textAlign: 'center', mb: 1 }}>
							<Typography
								variant="subtitle2"
								sx={{
									fontSize: isMobile ? '12px' : '14px',
									color: '#666',
									fontWeight: 500
								}}
							>
								{t('lastFiveMatches', { defaultValue: 'Last 5 matches' })}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 3, mb: 2 }}>
							{/* Left Column - PieChart & Form Guide */}
							<Box sx={{ flex: isMobile ? 'none' : '2', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
								<Box sx={{ width: '100%', height: isMobile ? 180 : 220 }}>
									{pieData.length > 0 ? (
										<ResponsiveContainer width="100%" height="100%">
											<PieChart>
												<Pie
													data={pieData}
													dataKey="value"
													nameKey="name"
													cx="50%"
													cy="50%"
													innerRadius={isMobile ? 38 : 50}
													outerRadius={isMobile ? 70 : 90}
													paddingAngle={effectivePaddingAngle}
													cornerRadius={8}
													label={renderCustomizedLabel}
													labelLine={false}
													stroke="#fff"
													strokeWidth={2}
													animationBegin={0}
													animationDuration={500}
												>
													{pieData.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={entry.color} />
													))}
												</Pie>
												<Tooltip />
											</PieChart>
										</ResponsiveContainer>
									) : (
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
											<Typography variant="body2">{t('noCompletedMatches')}</Typography>
										</Box>
									)}
								</Box>

								{/* Form Guide - Dots */}
								{pieData.length > 0 && (
									<Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center', alignItems: 'center' }}>
										<Typography variant="caption" sx={{ color: '#666', mr: 1, fontWeight: 600 }}>
											{t('form', { defaultValue: 'Form:' })}
										</Typography>
										{[...lastFiveMatches].reverse().map((match, idx) => (
											<MuiTooltip
												key={match.id}
												title={
													<>
														<div>
															{`${t(`teams.${match.homeTeam.name}`, { defaultValue: match.homeTeam.name })} vs ${t(`teams.${match.awayTeam.name}`, { defaultValue: match.awayTeam.name })} (${match.scoreHome}:${match.scoreAway})`}
														</div>
														<div>
															{t(`matchStatus.${match.matchStatus}`, { defaultValue: match.matchStatus })}
														</div>
													</>
												}
											>
												<Box
													sx={{
														width: 14,
														height: 14,
														borderRadius: '50%',
														background: getResultStyle(match),
														display: 'inline-block',
														boxShadow: '0 2px 2px rgba(0,0,0,0.1)'
													}}
												/>
											</MuiTooltip>
										))}
									</Box>
								)}
							</Box>

							{/* Right Column - Statistics */}
							<Box sx={{ flex: isMobile ? 'none' : '1', minWidth: 0 }}>
								<Box
									sx={{
										display: 'flex',
										flexDirection: isMobile ? 'row' : 'column',
										justifyContent: isMobile ? 'space-around' : 'center',
										height: isMobile ? 'auto' : 260,
										gap: isMobile ? 1 : 3,
										p: isMobile ? 1 : 2,
									}}
								>
									<Box sx={{ textAlign: 'center', flex: isMobile ? 1 : 'none' }}>
										<Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50', fontSize: isMobile ? '20px' : '32px', mb: 0.5 }}>
											{animatedGoalsScored.toFixed(1)}
										</Typography>
										<Typography variant="body2" sx={{ color: '#666', fontSize: isMobile ? '10px' : '13px', fontWeight: 500 }}>
											{t('avgGoalsScored', { defaultValue: 'Avg Goals Scored' })}
										</Typography>
									</Box>

									<Box sx={{ textAlign: 'center', flex: isMobile ? 1 : 'none' }}>
										<Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d9534f', fontSize: isMobile ? '20px' : '32px', mb: 0.5 }}>
											{animatedGoalsReceived.toFixed(1)}
										</Typography>
										<Typography variant="body2" sx={{ color: '#666', fontSize: isMobile ? '10px' : '13px', fontWeight: 500 }}>
											{t('avgGoalsReceived', { defaultValue: 'Avg Goals Received' })}
										</Typography>
									</Box>
								</Box>
							</Box>
						</Box>

						{/* NOVÉ: Filtr ligy vpravo nad tabulkou */}
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, mt: 1 }}>
							<FormControl size="small" sx={{ minWidth: 150, width: isMobile ? '100%' : 'auto' }}>
								<InputLabel id="league-filter-label" sx={{ fontSize: '13px' }}>{t('filterByLeague', { defaultValue: 'League' })}</InputLabel>
								<Select
									labelId="league-filter-label"
									value={selectedLeague}
									label={t('filterByLeague', { defaultValue: 'League' })}
									onChange={handleLeagueChange}
									sx={{ fontSize: '13px', height: 35 }}
								>
									<MenuItem value="all" sx={{ fontSize: '13px' }}><em>{t('allLeagues', { defaultValue: 'All Leagues' })}</em></MenuItem>
									{uniqueLeagues.map(league => (
										<MenuItem key={league} value={league} sx={{ fontSize: '13px' }}>
											{t(`league_names.${slugifyLeague(league)}`, { defaultValue: league })}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>

						<TableContainer component={Paper} sx={{ mt: 0, boxShadow: 'none' }}>
							<Table aria-label="team matches table" size={isMobile ? 'small' : 'medium'}>
								<TableHead>
									<TableRow>
										<TableCell sx={{ backgroundColor: '#F9F9F9', color: '#787878', textTransform: 'uppercase', fontSize: isMobile ? '12px' : '14px', border: 'none', p: isMobile ? '6px 8px' : '8px 16px' }} align="left">{t('date')}</TableCell>
										{/* NOVÉ: Sloupec s ligou (pouze Desktop) */}
										{!isMobile && (
											<TableCell sx={{ backgroundColor: '#F9F9F9', color: '#787878', textTransform: 'uppercase', fontSize: '14px', border: 'none' }} align="left">{t('league', { defaultValue: 'League' })}</TableCell>
										)}
										<TableCell sx={{ backgroundColor: '#F9F9F9', color: '#787878', textTransform: 'uppercase', fontSize: isMobile ? '12px' : '14px', border: 'none', p: isMobile ? '6px 8px' : '8px 16px' }} align="left">{t('match')}</TableCell>
										<TableCell sx={{ backgroundColor: '#F9F9F9', color: '#787878', textTransform: 'uppercase', fontSize: isMobile ? '12px' : '14px', border: 'none', p: isMobile ? '6px 8px' : '8px 16px' }} align="center">{t('score')}</TableCell>
										{!isMobile && (
											<TableCell sx={{ backgroundColor: '#F9F9F9', color: '#787878', textTransform: 'uppercase', fontSize: '14px', border: 'none' }} align="center">{t('result')}</TableCell>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{paginatedMatches.map((m) => {
										const { goalsFor, goalsAgainst } = getEffectiveGoals(m);
										const isOtOrPenalties = m.matchStatus === 'AOT' || m.matchStatus === 'AP' || m.matchStatus === 'AET' || m.matchStatus === 'PEN';
										const showPenaltyScore = m.matchStatus === 'PEN' &&
											m.penaltyScoreHome != null && m.penaltyScoreAway != null;

										let result = '';
										if (m.matchStatus === 'PST' || m.matchStatus === 'POST') result = t('postponed');
										else if (goalsFor > goalsAgainst) result = isOtOrPenalties ? t('win', { defaultValue: 'W (OT)' }) : t('win');
										else if (goalsFor < goalsAgainst) result = isOtOrPenalties ? t('lost', { defaultValue: 'L (OT)' }) : t('lost');
										else if (goalsFor === goalsAgainst) result = t('draw');

										const formattedDate = formatDate(m.date);
										const resultBackground = getResultStyle(m);
										const statusTooltip = t(`matchStatus.${m.matchStatus}`, { defaultValue: m.matchStatus });
										const matchRedCards = redCardsByMatch[m.id] || { home: 0, away: 0 };

										const chipSx = {
											display: 'inline-block',
											background: resultBackground,
											borderRadius: '8px',
											padding: isMobile ? '2px 6px' : '4px 8px',
											fontWeight: 'bold',
											color: 'white',
											ml: isMobile ? 1 : 0
										};

										const cellBaseSx = { border: 'none', fontSize: isMobile ? '12px' : '14px', p: isMobile ? '6px 8px' : '8px 16px' };
										return (
											<TableRow key={m.id}>
												<TableCell sx={cellBaseSx} align="left">{formattedDate}</TableCell>

												{/* NOVÉ: Hodnota sloupce liga (pouze Desktop) */}
												{!isMobile && (
													<TableCell sx={{ border: 'none', fontSize: '14px', color: '#555' }}>
														{t(`league_names.${slugifyLeague(m.leagueName)}`, { defaultValue: m.leagueName })}
													</TableCell>
												)}

												<TableCell sx={cellBaseSx} align="left">
													<Box sx={{ display: 'flex', flexDirection: 'column' }}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
															<Typography variant="body2" sx={{ fontWeight: m.scoreHome > m.scoreAway ? 'bold' : 'normal', fontSize: isMobile ? '12px' : '14px' }}>
																{t(`teams.${m.homeTeam.name}`, { defaultValue: m.homeTeam.name })}
															</Typography>
															{renderRedCard(matchRedCards.home)}
														</Box>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
															<Typography variant="body2" sx={{ fontWeight: m.scoreAway > m.scoreHome ? 'bold' : 'normal', fontSize: isMobile ? '12px' : '14px' }}>
																{t(`teams.${m.awayTeam.name}`, { defaultValue: m.awayTeam.name })}
															</Typography>
															{renderRedCard(matchRedCards.away)}
														</Box>
													</Box>
												</TableCell>
												<TableCell sx={cellBaseSx} align="center">
													{m.matchStatus === 'PST' || m.matchStatus === 'POST' ? (
														<Typography variant="body2">-</Typography>
													) : (
														<>
															<Typography variant="body2">{`${m.scoreHome} : ${m.scoreAway}`}</Typography>
															{showPenaltyScore && (
																<Typography
																	variant="body2"
																	sx={{ color: '#aaa', fontSize: isMobile ? '10px' : '12px', lineHeight: 1.2 }}
																>
																	{`(${m.penaltyScoreHome} : ${m.penaltyScoreAway})`}
																</Typography>
															)}
														</>
													)}
													{isMobile && (
														<MuiTooltip title={statusTooltip} arrow>
															<Box component="span" sx={chipSx}>
																<Typography variant="body2" component="span" sx={{ fontWeight: 'bold', fontSize: isMobile ? '10px' : '12px' }}>{result}</Typography>
															</Box>
														</MuiTooltip>
													)}
												</TableCell>
												{!isMobile && (
													<TableCell sx={{ border: 'none' }} align="center">
														<MuiTooltip title={statusTooltip} arrow>
															<Box sx={chipSx}>
																<Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>{result}</Typography>
															</Box>
														</MuiTooltip>
													</TableCell>
												)}
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
				{matches.length > 0 && (
					<TablePagination
						component="div"
						count={filteredMatches.length} // Upraveno na počet filtrovaných
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPageOptions={isMobile ? [] : [5, 10, 25, 100]}
						onRowsPerPageChange={isMobile ? undefined : handleChangeRowsPerPage}
						labelRowsPerPage={isMobile ? '' : t('rowsPerPage')}
						labelDisplayedRows={({ from, to, count }) => isMobile ? `${from}–${to} / ${count}` : `${from}—${to} ${t('of')} ${count}`}
						showFirstButton={!isMobile}
						showLastButton={!isMobile}
						sx={{ '.MuiTablePagination-toolbar': { p: isMobile ? 0 : 1 }, mt: 1 }}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default TeamMatchesModal;